import { useState } from "react";
import { HelpCircle, X, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { getVisibleSections, type HelpSection, type HelpArticle } from "@/data/helpManualContent";
import { cn } from "@/lib/utils";

/**
 * Map a route (and optional ?tab= query) to one or more help section IDs.
 * Valid section IDs come from src/data/helpManualContent.ts:
 *   getting-started, alerts, getting-help, platform-access, room-register,
 *   main-register, children, staff, rota, attendance, transitions,
 *   planned-changes, approvals, funding, plans-billing, compliance,
 *   audit-log, settings, security, out-of-school, pickups, whats-new,
 *   ofsted-readiness, kiosk-mode, ratios, additional-days, safeguarding-log,
 *   complaints-log, confidential-safeguarding, leads-and-succession
 */
function resolveSections(pathname: string, search: string): string[] {
  const params = new URLSearchParams(search);
  const tab = params.get("tab");

  // Exact / prefix routes (most specific first)
  if (pathname === "/dashboard" || pathname === "/") return ["getting-started", "whats-new"];

  if (pathname.startsWith("/children")) return ["children"];
  if (pathname.startsWith("/staff")) return ["staff", "platform-access"];
  if (pathname === "/rota" || pathname === "/my-rota") return ["rota", "ratios", "kiosk-mode"];
  if (pathname === "/register") return ["main-register", "room-register"];
  if (pathname === "/room-register" || pathname.startsWith("/zone/")) return ["room-register"];
  if (pathname === "/attendance-staffing") return ["attendance", "ratios"];
  if (pathname === "/reports") return ["attendance", "children"];

  // Compliance hub: include every documented sub-section so the user sees the
  // specific guidance for Risk Assessments, Fire Safety, Health & Medicines
  // and Qualifications without leaving the page.
  if (pathname.startsWith("/compliance")) return ["compliance", "ratios"];

  if (pathname.startsWith("/additional-days")) return ["additional-days"];
  if (pathname.startsWith("/safeguarding-log")) return ["safeguarding-log", "leads-and-succession"];
  if (pathname.startsWith("/complaints-log")) return ["complaints-log", "leads-and-succession"];
  if (pathname.startsWith("/confidential-safeguarding")) return ["confidential-safeguarding", "leads-and-succession"];
  if (pathname.startsWith("/ofsted-readiness")) return ["ofsted-readiness"];
  if (pathname.startsWith("/transitions")) return ["transitions"];
  if (pathname.startsWith("/planned-changes")) return ["planned-changes"];
  if (pathname.startsWith("/funding")) return ["funding"];
  if (pathname.startsWith("/alerts")) return ["alerts"];
  if (pathname.startsWith("/approvals")) return ["approvals"];
  if (pathname.startsWith("/audit-log")) return ["audit-log"];
  if (pathname.startsWith("/security")) return ["security"];
  if (pathname.startsWith("/access")) return ["platform-access", "settings", "leads-and-succession"];
  if (pathname.startsWith("/my-profile")) return ["platform-access"];
  if (pathname.startsWith("/pickups") || pathname === "/pickup") return ["pickups"];
  if (pathname.startsWith("/outings")) return ["compliance"];
  if (pathname.startsWith("/upgrade")) return ["plans-billing"];
  if (pathname === "/help") return ["getting-help"];

  // Settings is tab-aware so each sub-section gets its own help content.
  if (pathname.startsWith("/settings")) {
    switch (tab) {
      case "billing":
        return ["plans-billing"];
      case "kiosk":
        return ["kiosk-mode"];
      case "referrals":
        return ["plans-billing"];
      case "data-import":
        return ["settings"];
      case "migration":
        return ["settings"];
      case "navigation":
        return ["settings"];
      case "succession":
        return ["leads-and-succession"];
      case "safeguarding":
        return ["leads-and-succession", "confidential-safeguarding"];
      case "configuration":
      case "general":
      default:
        return ["settings"];
    }
  }

  return ["getting-started"];
}

interface ContextualHelpButtonProps {
  pathname: string;
  search?: string;
}

export function ContextualHelpButton({ pathname, search = "" }: ContextualHelpButtonProps) {
  const { role } = useAuth();
  const [expandedArticles, setExpandedArticles] = useState<Set<string>>(new Set());

  const sectionIds = resolveSections(pathname, search);
  const allSections = getVisibleSections(role);
  const relevantSections = allSections.filter(s => sectionIds.includes(s.id));

  if (relevantSections.length === 0) return null;

  const toggleArticle = (id: string) => {
    setExpandedArticles(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label="Page help">
          <HelpCircle className="h-4 w-4 text-muted-foreground" />
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[360px] sm:w-[420px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-base">Help for this page</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-5">
          {relevantSections.map(section => (
            <div key={section.id}>
              <h3 className="text-sm font-semibold flex items-center gap-1.5 mb-2">
                <span>{section.emoji}</span> {section.title}
              </h3>
              <div className="space-y-1.5">
                {section.articles.map(article => {
                  const isOpen = expandedArticles.has(article.id);
                  return (
                    <div key={article.id} className="border rounded-md bg-card">
                      <button
                        onClick={() => toggleArticle(article.id)}
                        className="w-full flex items-center gap-2 p-3 text-left hover:bg-muted/50 transition-colors"
                      >
                        {isOpen ? (
                          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        )}
                        <span className="text-sm font-medium">{article.title}</span>
                      </button>
                      {isOpen && (
                        <div className="px-3 pb-3 pt-0">
                          <div className="text-xs text-muted-foreground mb-2">{article.summary}</div>
                          {article.content.split("\n").map((line, i) => {
                            if (!line.trim()) return <br key={i} />;
                            const formatted = line.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
                            return (
                              <p
                                key={i}
                                className="text-xs leading-relaxed mb-1"
                                dangerouslySetInnerHTML={{ __html: formatted }}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
